import { Button, Chip } from '@emailforge/ui';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CohortRetention } from '@/components/dashboard/CohortRetention';
import { DeliveryHealthGrid } from '@/components/dashboard/DeliveryHealthCards';
import { ProviderSnapshotCard } from '@/components/dashboard/ProviderSnapshotCard';
import { UsageMeter } from '@/components/dashboard/UsageMeter';
import { TopBar } from '@/components/layout/TopBar';
import { Icon } from '@/components/ui/icons';
import {
  type DailyBucket,
  type StatsAggregate,
  useDashboardQuery,
  useDeliveryHealthQuery,
} from '@/features/analytics/analyticsApi';
import { useGetCurrentOrgQuery } from '@/features/organizations/organizationsApi';
import { useListSendsQuery } from '@/features/sends/sendsApi';

type Tab = 'delivery-health' | 'engagement';

export function DashboardScreen() {
  const navigate = useNavigate();
  const { data: org } = useGetCurrentOrgQuery();
  const { data, isLoading, error } = useDashboardQuery();
  const { data: deliveryHealthData } = useDeliveryHealthQuery();
  const { data: recent } = useListSendsQuery({ limit: 8 });
  // Default to Delivery Health: this is an EmailOps tool, not a marketing
  // dashboard. Engagement (open/click/cohort) lives in the secondary tab for
  // the user who explicitly wants it.
  const [tab, setTab] = useState<Tab>('delivery-health');

  const t30 = data?.totals30;
  const t7 = data?.totals7;
  const byDay = data?.byDay ?? [];
  const providerHealth = data?.providerHealth ?? [];
  const deliveryHealth = providerHealth[0];

  return (
    <>
      <TopBar
        crumbs={[org?.name ?? 'Workspace', 'Delivery Health']}
        right={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button size="sm" onClick={() => navigate('/app/templates')}>
              <Icon.search size={13} /> Search
              <span className="kbd mono" style={{ marginLeft: 6 }}>
                ⌘K
              </span>
            </Button>
            <Button variant="flame" size="sm" onClick={() => navigate('/app/gallery')}>
              <Icon.plus size={13} /> New template
            </Button>
          </div>
        }
      />

      <div
        style={{
          borderBottom: '1px solid var(--line)',
          background: 'var(--paper)',
          padding: '0 24px',
          display: 'flex',
          gap: 24,
        }}
      >
        {(['delivery-health', 'engagement'] as const).map((t) => {
          const label = t === 'delivery-health' ? 'Delivery Health' : 'Engagement';
          const active = tab === t;
          return (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{
                padding: '14px 0',
                fontSize: 13,
                fontWeight: active ? 600 : 500,
                color: active ? 'var(--ink)' : 'var(--ink-3)',
                border: 'none',
                background: 'transparent',
                borderBottom: `2px solid ${active ? 'var(--flame)' : 'transparent'}`,
                cursor: 'pointer',
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      <div style={{ flex: 1, padding: 24, background: 'var(--paper-2)', overflowY: 'auto' }}>
        {error && (
          <div
            className="mf-card"
            style={{
              padding: 16,
              marginBottom: 16,
              background: 'var(--flame-soft)',
              borderColor: 'var(--flame-line)',
              color: 'var(--flame-2)',
              fontSize: 13,
            }}
          >
            Couldn't load analytics. The API might be offline.
          </div>
        )}

        {tab === 'delivery-health' && (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1.4fr 1fr',
                gap: 20,
                marginBottom: 20,
              }}
            >
              <div
                className="mf-card"
                style={{
                  padding: '22px 24px',
                  position: 'relative',
                  overflow: 'hidden',
                  background: 'var(--paper)',
                }}
              >
                <div
                  className="mono"
                  style={{
                    fontSize: 10,
                    color: 'var(--flame-2)',
                    letterSpacing: 0.1,
                    textTransform: 'uppercase',
                  }}
                >
                  {greeting()}
                </div>
                <h1
                  className="display"
                  style={{ fontSize: 32, lineHeight: 1.1, margin: '6px 0 12px', letterSpacing: -0.02 }}
                >
                  Your deliverability is{' '}
                  <i style={{ color: 'var(--flame)' }}>{deliverabilityWord(data?.deliverabilityScore)}</i>
                  .{' '}
                  <span style={{ fontStyle: 'normal', color: 'var(--ink-2)', fontSize: 22 }}>
                    {deliverabilityFollowup(data?.deliverabilityScore, t7)}
                  </span>
                </h1>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button variant="flame" size="sm" onClick={() => navigate('/app/sends')}>
                    Review recent sends <Icon.arrow size={13} />
                  </Button>
                  <Button size="sm" onClick={() => navigate('/app/templates')}>
                    Send test email
                  </Button>
                </div>
                <div style={{ position: 'absolute', right: -20, top: -10, opacity: 0.35, pointerEvents: 'none' }}>
                  <svg width={220} height={220} viewBox="0 0 220 220" aria-hidden>
                    <circle cx={110} cy={110} r={90} fill="none" stroke="var(--flame-line)" strokeDasharray="2 4" />
                    <circle cx={110} cy={110} r={60} fill="none" stroke="var(--flame-line)" strokeDasharray="2 4" />
                    <circle cx={110} cy={110} r={30} fill="none" stroke="var(--flame-line)" />
                  </svg>
                </div>
              </div>

              <ProviderSnapshotCard primary={providerHealth[0]} byDay={byDay} />
            </div>

            {deliveryHealthData ? (
              <DeliveryHealthGrid data={deliveryHealthData} />
            ) : (
              <div className="mono" style={{ fontSize: 12, color: 'var(--ink-3)', padding: 40, textAlign: 'center' }}>
                Loading delivery health…
              </div>
            )}
          </>
        )}

        {tab === 'engagement' && <>
        <div
          className="mf-card"
          style={{
            padding: 28,
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            gap: 24,
            alignItems: 'center',
            marginBottom: 20,
            background: 'radial-gradient(circle at 90% 20%, var(--flame-soft) 0%, var(--paper) 50%)',
          }}
        >
          <div>
            <div
              className="mono"
              style={{ fontSize: 10.5, color: 'var(--flame-2)', textTransform: 'uppercase', letterSpacing: 0.08 }}
            >
              {greeting()}
            </div>
            <h1 className="display" style={{ fontSize: 32, margin: '6px 0 10px', letterSpacing: -0.02 }}>
              {org?.counts.templates ?? 0} templates ·{' '}
              <i style={{ color: 'var(--flame)' }}>
                {isLoading ? '—' : t7?.delivered.toLocaleString() ?? '0'} sends
              </i>{' '}
              this week
            </h1>
            <p style={{ fontSize: 13.5, color: 'var(--ink-2)', margin: 0 }}>
              {deliverabilityLine(data?.deliverabilityScore)}
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Button variant="flame" onClick={() => navigate('/app/gallery')}>
              New from gallery
            </Button>
            <Button onClick={() => navigate('/app/ab')}>
              Review A/B results
            </Button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
          <Kpi label="Delivered · 7d" current={t7?.delivered} previous={prev7(t30, t7)?.delivered} byDay={byDay} metric="delivered" />
          <Kpi label="Open rate · 7d" current={rateLabel(t7?.openRate)} previous={rateLabel(prev7Rate(t30, t7, 'openRate'))} byDay={byDay} metric="opened" percent />
          <Kpi label="Click rate · 7d" current={rateLabel(t7?.clickRate)} previous={rateLabel(prev7Rate(t30, t7, 'clickRate'))} byDay={byDay} metric="clicked" percent />
          <Kpi label="Bounce rate · 7d" current={rateLabel(t7?.bounceRate)} previous={rateLabel(prev7Rate(t30, t7, 'bounceRate'))} byDay={byDay} metric="bounced" percent invert />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 12, marginBottom: 20 }}>
          <div className="mf-card" style={{ padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 550 }}>Provider health</div>
            {deliveryHealth ? (
              <Chip variant="live">{(deliveryHealth.successRate * 100).toFixed(1)}% success
              </Chip>
            ) : (
              <Chip  style={{ background: 'var(--paper-3)' }}>No sends yet
              </Chip>
            )}
            <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
              last 30 days
            </span>
            <div style={{ flex: 1 }} />
            {deliveryHealth?.p50LatencyMs != null && (
              <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                p50 latency: {Math.round(deliveryHealth.p50LatencyMs)}ms
              </span>
            )}
          </div>
          <DailyBars byDay={byDay} />
          </div>
          <UsageMeter />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 12 }}>
          <div className="mf-card">
            <div className="mf-card-head">
              <span className="mf-card-title">Recent sends</span>
              <Chip variant="live">Live
              </Chip>
              <div style={{ flex: 1 }} />
              <Button variant="ghost" size="sm" onClick={() => navigate('/app/sends')}>
                View all
              </Button>
            </div>
            <table className="mf-table">
              <thead>
                <tr>
                  <th>Template</th>
                  <th>Recipient</th>
                  <th>Status</th>
                  <th>Latency</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {(recent ?? []).slice(0, 6).map((r) => (
                  <tr key={r.id}>
                    <td className="mono" style={{ fontSize: 12 }}>
                      {r.templateSlug ?? r.templateName ?? '—'}
                    </td>
                    <td className="mono" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                      {r.recipientHash.slice(0, 3)}…{r.recipientHash.slice(-3)}
                    </td>
                    <td>
                      <Chip variant={statusChipVariant(r.status)}>{r.status}</Chip>
                    </td>
                    <td className="mono" style={{ fontSize: 11.5 }}>
                      {r.latencyMs != null ? `${r.latencyMs}ms` : '—'}
                    </td>
                    <td className="mono" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                      {relativeTime(r.occurredAt)}
                    </td>
                  </tr>
                ))}
                {(recent ?? []).length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: 24, fontSize: 12.5, color: 'var(--ink-3)' }}>
                      No sends yet. Connect a provider and send your first email.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mf-card">
            <div className="mf-card-head">
              <span className="mf-card-title">Engagement · 30 days</span>
              <div style={{ flex: 1 }} />
              <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                open · click
              </span>
            </div>
            <div style={{ padding: 18 }}>
              <EngagementChart byDay={byDay} />
            </div>
            <div
              style={{
                padding: '10px 14px',
                borderTop: '1px solid var(--line-soft)',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <Chip>{rateLabel(t30?.openRate)} open
              </Chip>
              <Chip variant="flame">{rateLabel(t30?.clickRate)} click
              </Chip>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <CohortRetention rows={data?.cohortRetention ?? []} />
        </div>
        </>}
      </div>
    </>
  );
}

type KpiProps = {
  label: string;
  current: number | string | undefined;
  previous: number | string | undefined;
  byDay: DailyBucket[];
  metric: keyof Pick<DailyBucket, 'delivered' | 'opened' | 'clicked' | 'bounced'>;
  percent?: boolean;
  invert?: boolean;
};

function Kpi({ label, current, previous, byDay, metric, percent, invert }: KpiProps) {
  const up = useMemo(() => {
    if (typeof current !== 'number' || typeof previous !== 'number') return true;
    return invert ? current <= previous : current >= previous;
  }, [current, previous, invert]);

  const delta = useMemo(() => {
    if (typeof current !== 'number' || typeof previous !== 'number' || previous === 0) return '—';
    const d = current - previous;
    const sign = d >= 0 ? '+' : '';
    return percent ? `${sign}${(d * 100).toFixed(1)}pt` : `${sign}${Math.round(d)}`;
  }, [current, previous, percent]);

  const displayValue =
    typeof current === 'number'
      ? percent
        ? `${(current * 100).toFixed(1)}%`
        : current.toLocaleString()
      : (current ?? '—');

  return (
    <div className="mf-card" style={{ padding: 16 }}>
      <div
        className="mono"
        style={{ fontSize: 10.5, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: 0.06 }}
      >
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginTop: 6 }}>
        <span className="display" style={{ fontSize: 28, fontWeight: 500, letterSpacing: -0.01 }}>
          {displayValue}
        </span>
        <span
          className="mono"
          style={{ fontSize: 11, color: up ? 'var(--spark)' : 'var(--err)', marginBottom: 6 }}
        >
          {delta}
        </span>
      </div>
      <MiniSparkline byDay={byDay} metric={metric} up={up} />
    </div>
  );
}

function MiniSparkline({ byDay, metric, up }: { byDay: DailyBucket[]; metric: keyof DailyBucket; up: boolean }) {
  const series = byDay.slice(-16).map((d) => Number(d[metric] ?? 0));
  if (series.length < 2) {
    return <svg width="100%" height={28} viewBox="0 0 96 28" style={{ marginTop: 8 }} />;
  }
  const max = Math.max(...series, 1);
  const points = series
    .map((v, i) => {
      const x = (i / (series.length - 1)) * 96;
      const y = 26 - (v / max) * 22;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg width="100%" height={28} viewBox="0 0 96 28" style={{ marginTop: 8 }}>
      <polyline
        points={points}
        fill="none"
        stroke={up ? 'var(--spark)' : 'var(--err)'}
        strokeWidth={1.4}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

function DailyBars({ byDay }: { byDay: DailyBucket[] }) {
  const last30 = byDay.slice(-30);
  const max = Math.max(...last30.map((d) => d.delivered + d.bounced), 1);
  if (last30.length === 0) {
    return (
      <div
        style={{
          height: 48,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--ink-3)',
          fontSize: 12,
        }}
      >
        No delivery data yet.
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: 48 }}>
      {last30.map((d) => {
        const total = d.delivered + d.bounced;
        const h = Math.max(2, (total / max) * 46);
        const err = d.bounced > 0 && d.bounced / Math.max(total, 1) > 0.05;
        return (
          <div
            key={d.date}
            title={`${d.date}: ${d.delivered} delivered · ${d.bounced} bounced`}
            style={{
              flex: 1,
              height: `${h}px`,
              background: err ? 'var(--err)' : 'var(--spark)',
              opacity: 0.7,
              borderRadius: 2,
            }}
          />
        );
      })}
    </div>
  );
}

function EngagementChart({ byDay }: { byDay: DailyBucket[] }) {
  const width = 360;
  const height = 140;
  const series = byDay.slice(-14);
  if (series.length < 2) {
    return (
      <div
        style={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          color: 'var(--ink-3)',
        }}
      >
        Not enough data yet.
      </div>
    );
  }
  const max = Math.max(...series.map((d) => d.opened + d.clicked), 1);
  const openPts = series
    .map((d, i) => `${(i / (series.length - 1)) * width},${height - 10 - (d.opened / max) * (height - 20)}`)
    .join(' ');
  const clickPts = series
    .map((d, i) => `${(i / (series.length - 1)) * width},${height - 10 - (d.clicked / max) * (height - 20)}`)
    .join(' ');
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`}>
      {[0, 1, 2, 3].map((i) => (
        <line
          key={i}
          x1={0}
          x2={width}
          y1={(i * height) / 3}
          y2={(i * height) / 3}
          stroke="var(--line-soft)"
          strokeWidth={1}
        />
      ))}
      <polyline points={openPts} fill="none" stroke="var(--ink-2)" strokeWidth={1.6} />
      <polyline points={clickPts} fill="none" stroke="var(--flame)" strokeWidth={1.6} strokeDasharray="4 3" />
    </svg>
  );
}

function prev7(t30: StatsAggregate | undefined, t7: StatsAggregate | undefined) {
  if (!t30 || !t7) return undefined;
  return {
    delivered: Math.max(0, t30.delivered - t7.delivered),
    opened: Math.max(0, t30.opened - t7.opened),
    clicked: Math.max(0, t30.clicked - t7.clicked),
    bounced: Math.max(0, t30.bounced - t7.bounced),
  };
}

function prev7Rate(
  t30: StatsAggregate | undefined,
  t7: StatsAggregate | undefined,
  field: 'openRate' | 'clickRate' | 'bounceRate',
): number | undefined {
  const prev = prev7(t30, t7);
  if (!prev || !t30 || !t7) return undefined;
  const deliveredPrev = Math.max(0, t30.delivered - t7.delivered);
  if (deliveredPrev === 0) return undefined;
  if (field === 'openRate') return prev.opened / deliveredPrev;
  if (field === 'clickRate') return prev.clicked / deliveredPrev;
  return prev.bounced / deliveredPrev;
}

function rateLabel(n: number | undefined): number | undefined {
  return typeof n === 'number' ? n : undefined;
}

function statusChipVariant(status: string): 'flame' | 'err' | 'live' {
  if (status === 'clicked') return 'flame';
  if (status === 'failed' || status === 'bounced' || status === 'complained') return 'err';
  return 'live';
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function deliverabilityLine(score: number | undefined): string {
  if (score == null) return 'Connect a provider and send your first email to see metrics.';
  if (score >= 95) return `Deliverability is excellent (${score}/100).`;
  if (score >= 85) return `Deliverability is healthy (${score}/100). Watch bounce rate.`;
  return `Deliverability is ${score}/100 — review bounces and domain auth.`;
}

function deliverabilityWord(score: number | undefined): string {
  if (score == null) return 'pending';
  if (score >= 95) return 'excellent';
  if (score >= 85) return 'healthy';
  if (score >= 70) return 'okay';
  return 'at risk';
}

function deliverabilityFollowup(score: number | undefined, t7: StatsAggregate | undefined): string {
  if (score == null) return 'Send your first email to populate this dashboard.';
  if (t7 && t7.bounced > 0 && t7.delivered > 0 && t7.bounced / Math.max(t7.delivered, 1) > 0.02) {
    return 'Bounce rate is elevated this week — investigate provider errors.';
  }
  if (score >= 95) return 'No action needed — keep an eye on the latency tail.';
  if (score >= 85) return 'Watch bounces and provider error rates closely.';
  return 'Review bounces, retry recovery, and domain auth.';
}
